import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BarChart3,
  BookOpen,
  CheckCircle2,
  ChevronRight,
  Star,
  Target,
  TrendingUp,
  Users,
  Zap,
} from 'lucide-react';
import { getAllRoles, getRoleGroups } from '../../lib/rolesRegistry';
import { getSOPsForRole } from '../../lib/sopsRegistry';

// ── helpers ──────────────────────────────────────────────────────────────────
const isAr = () => document.documentElement.lang === 'ar';

// ── Role scorecard definitions ────────────────────────────────────────────────
// KPIs and targets defined per role, aligned with the pipeline registry data

interface RoleScorecard {
  roleId: string;
  kpis: { metric: string; metricAr: string; target: string; category: 'speed' | 'conversion' | 'quality' | 'customer' | 'finance' }[];
  reviewCycle: string;
  reviewCycleAr: string;
  escalationNote: string;
  escalationNoteAr: string;
}

const scorecardData: RoleScorecard[] = [
  {
    roleId: 'bdc_agent',
    kpis: [
      { metric: 'Speed-to-lead (digital)', metricAr: 'سرعة الوصول (رقمي)', target: '<3 min', category: 'speed' },
      { metric: 'Lead touched rate within SLA', metricAr: 'معدل اللمس ضمن مستوى الخدمة', target: '100%', category: 'quality' },
      { metric: 'Cadence completion rate', metricAr: 'معدل اكتمال التسلسل', target: '100%', category: 'quality' },
      { metric: 'Contact rate (cadence)', metricAr: 'معدل التواصل', target: '>30%', category: 'conversion' },
      { metric: 'Qualification rate', metricAr: 'معدل التأهيل', target: '>40%', category: 'conversion' },
      { metric: 'Appointment booking rate', metricAr: 'معدل حجز المواعيد', target: '>85%', category: 'conversion' },
      { metric: 'WhatsApp response rate', metricAr: 'معدل الرد عبر واتساب', target: '>25%', category: 'quality' },
    ],
    reviewCycle: 'Daily / Weekly',
    reviewCycleAr: 'يومي / أسبوعي',
    escalationNote: 'SLA breach triggers auto-reassignment and manager alert',
    escalationNoteAr: 'خرق مستوى الخدمة يؤدي إلى إعادة التخصيص التلقائي وتنبيه المدير',
  },
  {
    roleId: 'bdc_supervisor',
    kpis: [
      { metric: 'Team SLA compliance rate', metricAr: 'معدل امتثال الفريق لمستوى الخدمة', target: '>95%', category: 'quality' },
      { metric: 'Unreachable rate (team)', metricAr: 'معدل عدم الوصول (الفريق)', target: '<40%', category: 'conversion' },
      { metric: 'Data completeness (team avg)', metricAr: 'اكتمال البيانات (متوسط الفريق)', target: '>80%', category: 'quality' },
      { metric: 'Escalations handled', metricAr: 'حالات التصعيد المعالجة', target: '100% within SLA', category: 'quality' },
      { metric: 'Coaching sessions completed', metricAr: 'جلسات التدريب المكتملة', target: 'Weekly per agent', category: 'quality' },
    ],
    reviewCycle: 'Weekly / Monthly',
    reviewCycleAr: 'أسبوعي / شهري',
    escalationNote: 'Monitors team SLA compliance and handles exception approvals',
    escalationNoteAr: 'يراقب امتثال الفريق ويتعامل مع موافقات الاستثناء',
  },
  {
    roleId: 'sales_advisor',
    kpis: [
      { metric: 'Show rate (appointments kept)', metricAr: 'معدل حضور المواعيد', target: '>70%', category: 'conversion' },
      { metric: 'Showroom-to-quote rate', metricAr: 'معدل تحويل المعرض للعروض', target: '>70%', category: 'conversion' },
      { metric: 'Quote follow-up compliance', metricAr: 'الالتزام بمتابعة العروض', target: '100%', category: 'quality' },
      { metric: 'Negotiation-to-booking rate', metricAr: 'معدل تحويل التفاوض للحجز', target: '>60%', category: 'conversion' },
      { metric: 'Post-delivery CSAT score', metricAr: 'درجة رضا العميل بعد التسليم', target: '>4.5/5', category: 'customer' },
      { metric: 'Referral generation rate', metricAr: 'معدل توليد الإحالات', target: '>15%', category: 'customer' },
      { metric: 'Day 2 follow-up completion', metricAr: 'اكتمال متابعة اليوم 2', target: '100%', category: 'quality' },
    ],
    reviewCycle: 'Weekly / Monthly',
    reviewCycleAr: 'أسبوعي / شهري',
    escalationNote: 'Owns the customer relationship from appointment to post-sale',
    escalationNoteAr: 'يمتلك علاقة العميل من الموعد حتى ما بعد البيع',
  },
  {
    roleId: 'sales_manager',
    kpis: [
      { metric: 'Branch conversion rate (Lead → Won)', metricAr: 'معدل تحويل الفرع', target: '>8%', category: 'conversion' },
      { metric: 'Average deal cycle time', metricAr: 'متوسط وقت دورة الصفقة', target: '<5 days', category: 'speed' },
      { metric: 'Booking cancellation rate', metricAr: 'معدل إلغاء الحجز', target: '<5%', category: 'finance' },
      { metric: 'Discount approval turnaround', metricAr: 'وقت الموافقة على الخصم', target: '<30 min', category: 'speed' },
      { metric: 'Team show rate', metricAr: 'معدل حضور الفريق', target: '>70%', category: 'conversion' },
      { metric: 'VIP/Key account satisfaction', metricAr: 'رضا العملاء الرئيسيين', target: '>4.8/5', category: 'customer' },
    ],
    reviewCycle: 'Monthly / Quarterly',
    reviewCycleAr: 'شهري / ربع سنوي',
    escalationNote: 'Approves high-value exceptions and manages team targets',
    escalationNoteAr: 'يوافق على الاستثناءات عالية القيمة ويدير أهداف الفريق',
  },
  {
    roleId: 'captain',
    kpis: [
      { metric: 'Test drive conversion rate', metricAr: 'معدل تحويل تجربة القيادة', target: '>60%', category: 'conversion' },
      { metric: 'Test drive safety incidents', metricAr: 'حوادث سلامة تجربة القيادة', target: '0', category: 'quality' },
      { metric: 'Customer satisfaction (test drive)', metricAr: 'رضا العميل (تجربة القيادة)', target: '>4.5/5', category: 'customer' },
      { metric: 'Vehicle preparation compliance', metricAr: 'الامتثال لتحضير السيارة', target: '100%', category: 'quality' },
    ],
    reviewCycle: 'Weekly / Monthly',
    reviewCycleAr: 'أسبوعي / شهري',
    escalationNote: 'Owns test drive experience and vehicle safety compliance',
    escalationNoteAr: 'يمتلك تجربة القيادة والامتثال لسلامة المركبة',
  },
  {
    roleId: 'finance_officer',
    kpis: [
      { metric: 'Finance approval rate', metricAr: 'معدل الموافقة على التمويل', target: '>80%', category: 'finance' },
      { metric: 'Finance processing time', metricAr: 'وقت معالجة التمويل', target: '<48 hours', category: 'speed' },
      { metric: 'Document completeness', metricAr: 'اكتمال المستندات', target: '100%', category: 'quality' },
      { metric: 'Contract error rate', metricAr: 'معدل أخطاء العقود', target: '<1%', category: 'quality' },
    ],
    reviewCycle: 'Weekly / Monthly',
    reviewCycleAr: 'أسبوعي / شهري',
    escalationNote: 'Manages bank liaison and finance exception escalations',
    escalationNoteAr: 'يدير التواصل مع البنك وتصعيد استثناءات التمويل',
  },
  {
    roleId: 'tradein_evaluator',
    kpis: [
      { metric: 'Trade-in valuation turnaround', metricAr: 'وقت تقييم السيارة القديمة', target: '<24 hours', category: 'speed' },
      { metric: 'Valuation accuracy (vs final offer)', metricAr: 'دقة التقييم', target: '<5% variance', category: 'quality' },
      { metric: 'Trade-in capture rate at showroom', metricAr: 'معدل التقاط التبديل في المعرض', target: 'Benchmark', category: 'conversion' },
    ],
    reviewCycle: 'Weekly',
    reviewCycleAr: 'أسبوعي',
    escalationNote: 'Provides accurate valuations that support deal closure',
    escalationNoteAr: 'يقدم تقييمات دقيقة تدعم إغلاق الصفقات',
  },
  {
    roleId: 'delivery_coordinator',
    kpis: [
      { metric: 'On-time delivery rate', metricAr: 'معدل التسليم في الوقت المحدد', target: '>95%', category: 'quality' },
      { metric: 'Day 7 follow-up completion', metricAr: 'اكتمال متابعة اليوم 7', target: '100%', category: 'quality' },
      { metric: 'CSAT at delivery', metricAr: 'رضا العميل عند التسليم', target: '>4.5/5', category: 'customer' },
      { metric: 'PDI completion before delivery', metricAr: 'اكتمال الفحص قبل التسليم', target: '100%', category: 'quality' },
      { metric: 'Complaints within 7 days', metricAr: 'الشكاوى خلال 7 أيام', target: '<5%', category: 'customer' },
    ],
    reviewCycle: 'Weekly / Monthly',
    reviewCycleAr: 'أسبوعي / شهري',
    escalationNote: 'Ensures ceremony-quality delivery and structured post-sale follow-up',
    escalationNoteAr: 'يضمن تسليماً احتفالياً ومتابعة منظمة بعد البيع',
  },
  {
    roleId: 'crm_admin',
    kpis: [
      { metric: 'System uptime / availability', metricAr: 'وقت تشغيل النظام', target: '>99.5%', category: 'quality' },
      { metric: 'Duplicate detection accuracy', metricAr: 'دقة اكتشاف التكرار', target: '>95%', category: 'quality' },
      { metric: 'Data integrity score', metricAr: 'درجة سلامة البيانات', target: '>98%', category: 'quality' },
      { metric: 'SOP update turnaround', metricAr: 'وقت تحديث الإجراءات', target: '<48 hours', category: 'speed' },
    ],
    reviewCycle: 'Monthly',
    reviewCycleAr: 'شهري',
    escalationNote: 'Owns system health, data integrity, and process governance',
    escalationNoteAr: 'يمتلك صحة النظام وسلامة البيانات وحوكمة العمليات',
  },
  {
    roleId: 'quality_analyst',
    kpis: [
      { metric: 'QA audits completed per week', metricAr: 'عمليات تدقيق الجودة أسبوعياً', target: '≥10 per agent', category: 'quality' },
      { metric: 'Compliance score (avg team)', metricAr: 'درجة الامتثال (متوسط الفريق)', target: '>90%', category: 'quality' },
      { metric: 'Coaching follow-through rate', metricAr: 'معدل متابعة التدريب', target: '>80%', category: 'quality' },
      { metric: 'SLA breach rate (team)', metricAr: 'معدل خرق مستوى الخدمة (الفريق)', target: '<5%', category: 'quality' },
    ],
    reviewCycle: 'Weekly / Monthly',
    reviewCycleAr: 'أسبوعي / شهري',
    escalationNote: 'Independent quality monitor — reports to branch/exec management',
    escalationNoteAr: 'مراقب جودة مستقل — يرفع تقاريره للإدارة',
  },
];

const categoryStyle: Record<string, { label: string; labelAr: string; color: string; bg: string; icon: React.ElementType }> = {
  speed:      { label: 'Speed', labelAr: 'السرعة', color: 'text-amber-700', bg: 'bg-amber-50 border-amber-200', icon: Zap },
  conversion: { label: 'Conversion', labelAr: 'التحويل', color: 'text-emerald-700', bg: 'bg-emerald-50 border-emerald-200', icon: TrendingUp },
  quality:    { label: 'Quality', labelAr: 'الجودة', color: 'text-blue-700', bg: 'bg-blue-50 border-blue-200', icon: CheckCircle2 },
  customer:   { label: 'Customer', labelAr: 'العميل', color: 'text-violet-700', bg: 'bg-violet-50 border-violet-200', icon: Star },
  finance:    { label: 'Finance', labelAr: 'التمويل', color: 'text-rose-700', bg: 'bg-rose-50 border-rose-200', icon: Target },
};

// ── Component ──────────────────────────────────────────────────────────────────

const Scorecards = () => {
  const ar = isAr();
  const [selected, setSelected] = useState<string>('bdc_agent');

  const allRoles = getAllRoles();
  const roleGroups = getRoleGroups();

  const currentCard = scorecardData.find(c => c.roleId === selected);
  const currentRole = allRoles.find(r => r.id === selected);
  const currentGroup = currentRole ? roleGroups.find(g => g.id === currentRole.group) : null;
  const relatedSOPs = currentRole ? getSOPsForRole(selected) : [];

  // Count by category in current card
  const catCounts = currentCard
    ? currentCard.kpis.reduce((acc, kpi) => {
        acc[kpi.category] = (acc[kpi.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    : {};

  return (
    <div className="space-y-8 animate-fade-in">

      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Link to="/performance" className="text-gray-400 hover:text-gray-600 transition-colors">
            <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          </Link>
          <span className="text-gray-400 text-sm">/</span>
          <span className="text-sm text-gray-500">{ar ? 'إدارة الأداء' : 'Performance'}</span>
        </div>
        <h1 className="text-3xl font-bold text-gray-900">
          {ar ? 'بطاقات نتائج الفريق' : 'Team Scorecards'}
        </h1>
        <p className="mt-1 text-gray-500 text-sm">
          {ar
            ? 'مؤشرات الأداء الرئيسية وأهداف الفريق لكل دور في نظام إدارة العملاء المحتملين'
            : 'KPI targets and performance expectations per role in the lead management operating system'}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">

        {/* Role selector sidebar */}
        <div className="lg:col-span-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3 px-1">
            {ar ? 'اختر الدور' : 'Select Role'}
          </p>
          <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
            {scorecardData.map((card, idx) => {
              const role = allRoles.find(r => r.id === card.roleId);
              const group = role ? roleGroups.find(g => g.id === role.group) : null;
              const gc = group?.color || '#6b7280';
              const isActive = selected === card.roleId;
              return (
                <button
                  key={card.roleId}
                  onClick={() => setSelected(card.roleId)}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left rtl:text-right transition-colors ${
                    isActive ? 'bg-primary-50 border-r-2 rtl:border-l-2 rtl:border-r-0 border-primary-600' : 'hover:bg-gray-50'
                  } ${idx < scorecardData.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                    style={{ backgroundColor: gc }}
                  >
                    {(role?.title || card.roleId)[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-primary-800' : 'text-gray-700'}`}>
                      {ar ? (role?.titleAr || card.roleId) : (role?.title || card.roleId)}
                    </p>
                    <p className="text-xs text-gray-400">{card.kpis.length} KPIs</p>
                  </div>
                  {isActive && <ChevronRight className="h-3.5 w-3.5 text-primary-500 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Scorecard main panel */}
        <div className="lg:col-span-3 space-y-5">
          {currentCard && currentRole ? (
            <>
              {/* Role header */}
              <div className="bg-white border border-gray-200 rounded-2xl p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                      style={{ backgroundColor: currentGroup?.color || '#6b7280' }}
                    >
                      <Users className="h-7 w-7" />
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">
                        {ar ? currentRole.titleAr : currentRole.title}
                      </h2>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {ar ? currentRole.departmentAr : currentRole.department}
                        {currentGroup && (
                          <span
                            className="ml-2 rtl:mr-2 rtl:ml-0 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                            style={{ backgroundColor: (currentGroup.color || '#6b7280') + '20', color: currentGroup.color || '#6b7280' }}
                          >
                            {ar ? currentGroup.labelAr : currentGroup.label}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="text-right rtl:text-left flex-shrink-0">
                    <p className="text-xs text-gray-500">{ar ? 'دورة المراجعة' : 'Review cycle'}</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {ar ? currentCard.reviewCycleAr : currentCard.reviewCycle}
                    </p>
                  </div>
                </div>

                {/* KPI category breakdown chips */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100">
                  {Object.entries(catCounts).map(([cat, count]) => {
                    const style = categoryStyle[cat];
                    if (!style) return null;
                    return (
                      <span key={cat} className={`inline-flex items-center gap-1.5 text-xs px-3 py-1 rounded-full border font-medium ${style.bg} ${style.color}`}>
                        <style.icon className="h-3 w-3" />
                        {ar ? style.labelAr : style.label}: {count}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* KPI Table */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    {ar ? 'مؤشرات الأداء الرئيسية' : 'Performance KPIs'} · {currentCard.kpis.length} {ar ? 'مؤشر' : 'metrics'}
                  </p>
                </div>
                <div className="divide-y divide-gray-100">
                  {currentCard.kpis.map((kpi, idx) => {
                    const style = categoryStyle[kpi.category];
                    return (
                      <div key={idx} className="flex items-center gap-4 px-5 py-3.5 hover:bg-gray-50 transition-colors">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full border font-medium flex-shrink-0 ${style?.bg || 'bg-gray-50 border-gray-200'} ${style?.color || 'text-gray-600'}`}>
                          {ar ? (style?.labelAr || kpi.category) : (style?.label || kpi.category)}
                        </span>
                        <p className="text-sm text-gray-800 flex-1">{ar ? kpi.metricAr : kpi.metric}</p>
                        <div className="text-right rtl:text-left flex-shrink-0">
                          <span className="inline-flex items-center px-3 py-1 bg-white border border-gray-200 rounded-lg text-sm font-bold text-gray-800 shadow-sm">
                            {kpi.target}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Escalation note + Related SOPs */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                  <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">
                    {ar ? 'ملاحظة التصعيد' : 'Escalation Note'}
                  </p>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    {ar ? currentCard.escalationNoteAr : currentCard.escalationNote}
                  </p>
                </div>
                <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-4">
                  <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wide mb-2">
                    <BookOpen className="h-3.5 w-3.5 inline mr-1 rtl:ml-1 rtl:mr-0" />
                    {ar ? 'إجراءات مرتبطة' : 'Related SOPs'} ({relatedSOPs.length})
                  </p>
                  <div className="space-y-1.5">
                    {relatedSOPs.slice(0, 4).map(sop => (
                      <Link
                        key={sop.meta.id}
                        to={`/sops/${sop.meta.id}`}
                        className="flex items-center gap-2 text-sm text-cyan-800 hover:text-cyan-900 group"
                      >
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sop.meta.priority === 'critical' ? 'bg-rose-500' : 'bg-cyan-400'}`} />
                        <span className="truncate">{ar ? sop.meta.titleAr : sop.meta.title}</span>
                        <ChevronRight className="h-3 w-3 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </Link>
                    ))}
                    {relatedSOPs.length === 0 && (
                      <p className="text-sm text-cyan-600">{ar ? 'لا توجد إجراءات مرتبطة' : 'No related SOPs'}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Links */}
              <div className="flex items-center gap-3 flex-wrap">
                <Link
                  to={`/roles/${currentRole.id}`}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Users className="h-4 w-4" />
                  {ar ? 'الملف الكامل للدور' : 'Full role profile'}
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
                <Link
                  to="/performance/kpis"
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <BarChart3 className="h-4 w-4" />
                  {ar ? 'إطار KPI الكامل' : 'Full KPI Framework'}
                  <ChevronRight className="h-3.5 w-3.5" />
                </Link>
              </div>
            </>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-gray-400">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>{ar ? 'اختر دوراً لعرض بطاقة النتائج' : 'Select a role to view its scorecard'}</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Scorecards;
